import {
  AccordionItemContentElement,
  AccordionItemElement,
  AccordionItemHeaderElement,
} from '@typedream/core';
import {
  getNode,
  getParentNode,
  insertNodes, isElementEmpty,
  liftNodes,
  moveNodes,
  removeNodes,
  TNodeEntry,
  withoutNormalizing,
  wrapNodes,
} from '@udecode/plate-core';
import { Path } from 'slate';
import {
  ELEMENT_ACCORDION,
  ELEMENT_ACCORDION_ITEM,
  ELEMENT_ACCORDION_ITEM_CONTENT,
  ELEMENT_ACCORDION_ITEM_HEADER,
  ELEMENT_CONTAINER,
  ELEMENT_H3,
} from '../../../pluginKeys';
import { DreamEditor } from '../../../types';

export const normalizeAccordionItem = (
  editor: DreamEditor,
  entry: TNodeEntry<AccordionItemElement>,
) => {
  const [node, path] = entry;

  const parent = getParentNode(editor, path);
  const firstChild = getNode<AccordionItemHeaderElement>(node, [0]);
  const secondChild = getNode<AccordionItemContentElement>(node, [1]);

  // remove empty accordion item
  if (isElementEmpty(editor, node)) {
    removeNodes(editor, { at: path });
    return true;
  }

  // type doesn't allow the children.length to be 1, but real data can.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (node.children.length === 1) {
    // add header if none exists
    if (firstChild?.type !== ELEMENT_ACCORDION_ITEM_HEADER) {
      insertNodes(
        editor,
        {
          type: ELEMENT_ACCORDION_ITEM_HEADER,
          children: [
            {
              type: ELEMENT_H3,
              children: [
                {
                  text: 'Accordion Item Header',
                },
              ],
            },
          ],
        },
        { at: [...path, 0] },
      );

      return true;
    }

    // add content
    insertNodes(
      editor,
      {
        type: ELEMENT_ACCORDION_ITEM_CONTENT,
        children: [{ text: '' }],
      },
      { at: [...path, 1] },
    );

    return true;
  }

  // remove non header first child
  if (!firstChild || firstChild.type !== ELEMENT_ACCORDION_ITEM_HEADER) {
    removeNodes(editor, { at: [...path, 0] });
    return true;
  }

  // remove non content second child
  if (!secondChild || secondChild.type !== ELEMENT_ACCORDION_ITEM_CONTENT) {
    removeNodes(editor, { at: [...path, 1] });
    return true;
  }

  // remove extra children
  if (node.children.length > 2) {
    removeNodes(editor, { at: [...path, 2] });
    return true;
  }

  // turn into container if parent is not accordion
  if (parent?.[0].type !== ELEMENT_ACCORDION) {
    withoutNormalizing(editor, () => {
      wrapNodes(
        editor,
        { type: ELEMENT_CONTAINER, children: [] },
        { at: path, match: (_, p) => Path.isChild(p, path) },
      );
      liftNodes(editor, {
        at: [...path, 0],
      });
    });

    return true;
  }

  return false;
};

export const normalizeAccordionItemHeader = (
  editor: DreamEditor,
  entry: TNodeEntry<AccordionItemHeaderElement>,
) => {
  const [node, path] = entry;

  const parent = getParentNode(editor, path);

  // lift node children up if parent is not accordion item
  if (parent?.[0].type !== ELEMENT_ACCORDION_ITEM) {
    liftNodes(editor, {
      at: path,
      match: (_, p) => Path.isChild(p, path),
    });

    return true;
  }

  if (node.children.length > 1) {
    moveNodes(editor, {
      at: [...path, 1],
      to: [...(Path.next(path)), 0],
    });
    return true;
  }

  return false;
};

export const normalizeAccordionItemContent = (
  editor: DreamEditor,
  entry: TNodeEntry<AccordionItemContentElement>,
) => {
  const [, path] = entry;

  const parent = getParentNode(editor, path);

  // lift node children up if parent is not accordion item
  if (parent?.[0].type !== ELEMENT_ACCORDION_ITEM) {
    liftNodes(editor, {
      at: path,
      match: (_, p) => Path.isChild(p, path),
    });

    return true;
  }

  return false;
};
